import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'
import $store from '@/store'

import Log from '@/views/Log/models/log'

import RoleAdd from '@/views/Authorization/dialogs/roleAdd/roleAdd.vue'
import RoleEdit from '@/views/Authorization/dialogs/roleEdit/roleEdit.vue'
import RoleDelete from '@/views/Authorization/dialogs/roleDelete/roleDelete.vue'
import RoleInfo from '@/views/Authorization/dialogs/roleInfo/roleInfo.vue'
import RemoveAccessPermissionFromLastOwnRole from '@/views/Authorization/dialogs/removeAccessPermissionFromLastOwnRole/removeAccessPermissionFromLastOwnRole.vue'
import ProjectUser from '@/views/Authorization/models/projectUser'
import router from '@/_router'

export default class Role extends JsonApiObject {
    public constructor(data: any, included?) {
        super(data, included)
    }

    get isNormal() {
        return this['kind'] == 'normal'
    }

    get isContractorRole() {
        return this['isSuggestedContractorRole']
    }

    get hasAuthorizationManageAccess() {
        return this.permissions.includes('authorization.manage_access')
    }

    get activePermissions() {
        return this._relationships['permissions']
            ? this._relationships['permissions'].map((permission) => permission._id)
            : []
    }

    get includedPermissions() {
        return this._relationships['includedPermissions']
            ? this._relationships['includedPermissions'].map((permission) => permission._id)
            : []
    }

    get availablePermissions() {
        return this._relationships['availablePermissions']
            ? this._relationships['availablePermissions'].map((permission) => permission._id)
            : []
    }

    get permissions() {
        return [...new Set([...this.activePermissions, ...this.includedPermissions])]
    }

    get permissionGroups() {
        return [...new Set(this.permissions.map((item) => item.split('.')[0]))].sort()
    }

    get grouppedPermissions() {
        return this.permissionGroups.map((group) =>
            this.permissions.filter((item) => item.includes(`${group}.`))
        )
    }

    hasPermission(permission: string | null) {
        return permission ? this.activePermissions.includes(permission) : false
    }

    hasPermissionIncluded(permission: string) {
        return this.includedPermissions.includes(permission)
    }

    isAvailablePermission(permission: string) {
        return this.availablePermissions.includes(permission)
    }

    static async getRoles(projectId: string) {
        let query = {
            include: [
                'roles.permissions',
                'roles.includedPermissions',
                'roles.availablePermissions',
            ],
        }

        let res = await Vue.prototype.$vbit.get('authorization', `projects/${projectId}`, query)

        let roles = res.included.filter((item) => item._type == 'role')

        $store.commit('roles', roles)

        return roles
    }

    async togglePermission(permission: string, projectUser: ProjectUser) {
        let rolePermissions = new Set(this.activePermissions)

        // if user aproved patching role or not
        let allow: any = true
        // If user removing access permission from the last role where he has access permission we need to redirect him
        let redirect = false

        if (
            (permission == 'authorization.manage_access' || permission == 'project.admin') &&
            this.hasAuthorizationManageAccess &&
            projectUser.isCurrentUser &&
            projectUser.accessRolesArr.includes(this._id) &&
            projectUser.accessRolesArr.length == 1
        ) {
            allow = await JsonApiObject.openDialog(RemoveAccessPermissionFromLastOwnRole, {
                role: this['name'],
            })
            redirect = allow
        }

        if (allow) {
            if (this.activePermissions.includes(permission)) {
                rolePermissions.delete(permission)
            } else {
                rolePermissions.add(permission)
            }

            let data = {
                relationships: {
                    permissions: [...rolePermissions].map((permission) => {
                        return { _type: 'permission', _id: permission }
                    }),
                },
            }

            try {
                await this.patchRole(data)
            } catch (error) {
                //
            }
        }

        if (redirect) {
            router.push({ name: 'user' })
        }
    }

    async editRole(role: Role) {
        let confirm = await JsonApiObject.openDialog(RoleEdit, { role: role })
        return confirm
    }

    async patchRole(data) {
        let body = this.formateBody(data.attributes, data.relationships)

        if (body) {
            let query = {
                include: ['permissions', 'includedPermissions', 'availablePermissions'],
                body: {
                    data: {
                        id: this._id,
                        type: this._type,
                        ...body,
                    },
                },
            }

            let res = await Vue.prototype.$vbit.patch('authorization', `roles/${this._id}`, query)

            this.initalize(res.data, res.included)

            return res.data
        }
    }

    async removeRole(role: Role) {
        let confirm = await JsonApiObject.openDialog(RoleDelete, { role: role })
        return confirm
    }

    async deleteRole() {
        let query = {
            updatedAt: this['updatedAt'],
        }
        await Vue.prototype.$vbit.delete('authorization', `roles/${this._id}`, query)
    }

    static async addRole() {
        let confirm = await JsonApiObject.openDialog(RoleAdd, {})
        return confirm
    }

    static async postRole(data) {
        let query = {
            include: ['permissions', 'includedPermissions', 'availablePermissions'],
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.post('authorization', `roles`, query)

        return new Role(res.data, res.included)
    }

    // Open dialog in project User settings that shows all the permissions that are added to this role and gives you access to view role log history
    async showRoleInfo(role) {
        await JsonApiObject.openDialog(RoleInfo, { role: role })
    }

    async getRoleLog(projectId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': projectId,
            'filter[activityLogNumber]': ['11'],
            'filter[entityId]': this._id,
            'filter[time][lt]': timeStamp,
        }

        let res = await Log.getLog(data)

        return res
    }
}
